import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, Globe } from 'lucide-react';
import { setCanonical, setMeta } from '../utils/seo';
import { SponsorBanner, AdSensePlaceholder } from './AdSlots';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BrandsDirectory() {
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setCanonical('/brands');
    setMeta('All Brands Size Charts', 'Complete size charts for 50+ clothing brands. Nike, Adidas, Zara, H&M, and more.');
    axios.get(`${API}/brands?limit=500`).then(r => setBrands(r.data)).catch(() => {});
  }, []);

  const origins = [...new Set(brands.map(b => b.country_origin))].sort();
  const filtered = brands.filter(b => {
    const matchSearch = !search || b.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || b.country_origin === filter;
    return matchSearch && matchFilter;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="mb-10">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#C5A028] mb-2">Complete Directory</p>
        <h1 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-light tracking-tighter text-[#1C1917] mb-4">All Brand Size Charts</h1>
        <p className="text-[#78716C] max-w-xl">Size charts and fit guides for 50+ global brands. Search by name or filter by country of origin.</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#78716C]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search brands..." className="w-full pl-10 pr-4 py-3 bg-[#FAFAFA] border border-[#E8E2D9] text-sm focus:border-[#C5A028] focus:outline-none" aria-label="Search brands" data-testid="brands-search" />
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter('all')} className={`px-4 py-2 text-xs font-medium border transition-colors ${filter === 'all' ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid="filter-all">All</button>
          {origins.map(o => (
            <button key={o} onClick={() => setFilter(o)} className={`px-4 py-2 text-xs font-medium border transition-colors ${filter === o ? 'bg-[#1C1917] text-white border-[#1C1917]' : 'bg-white text-[#78716C] border-[#E8E2D9] hover:border-[#C5A028]'}`} data-testid={`filter-${o}`}>{o}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-[#78716C] mb-6">{filtered.length} brands found</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filtered.map(b => (
          <Link key={b.slug} to={`/brand/${b.slug}`} className="bg-white p-5 border border-[#E8E2D9] hover:border-[#C5A028] hover:shadow-md transition-all duration-300 group" data-testid={`brand-dir-${b.slug}`}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="font-semibold text-[#1C1917] group-hover:text-[#C5A028] transition-colors">{b.name}</h3>
              <span className="text-xs bg-[#FAF8F4] px-2 py-0.5 text-[#78716C] flex items-center gap-1"><Globe className="w-3 h-3" />{b.country_origin}</span>
            </div>
            <p className="text-xs text-[#78716C] line-clamp-2 mb-3">{b.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#78716C]">{b.categories?.length || 0} size charts</span>
              <ArrowRight className="w-4 h-4 text-[#C5A028] opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        ))}
      </div>
      <SponsorBanner />
      <AdSensePlaceholder />
    </div>
  );
}
